<template>
  <EditableComponent
    v-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
    :id="id"
    :duplicate="componentPermissions.canDuplicate"
    :remove="componentPermissions.canRemove"
    :selected="componentSelectedMode && componentSelectedMode.id === id"
    settings
    @duplicate="duplicate(id)"
    @remove="removeComponent(id)"
    @select="setComponentSelectedMode(id)"
    @settings="setComponentSettingsMode(id)"
  >
    <div class="pa-4 w-full" v-bind="$attrs">
      <div class="flex gap-2 items-center border-primary5">
        <div class="flex-1 h-[2px] bg-primary3" />
        <div class="color-primary3">
          {{
            tracking_id !== ComponentTypes.PageBreak
              ? tracking_id
              : t('canvasUI.canvasBuilder.components.titles.pageBreak')
          }}
        </div>
        <div class="flex-1 h-[2px] bg-primary3" />
      </div>
      <div v-if="showUnnecessaryPageBreakWarning" class="mt-3 color-primary4 text-center">
        {{ t('canvasUI.canvasBuilder.components.pageBreak.unnecessaryWarning') }}
      </div>
    </div>
  </EditableComponent>
  <div v-else-if="isPrintDisplayMode" class="break-after-page m-0 p-0" v-bind="$attrs" />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { type ComponentNode, type PageBreakProps, ComponentTypes } from '@canvas-builder/types/canvas'
import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    data: PageBreakProps
    visible?: ComponentNode['visible']
    tracking_id?: string
  }>(),
  {},
)
const { t } = useI18n()
const {
  canvasContent,
  componentSelectedMode,
  isEditMode,
  isViewOnlyMode,
  isPrintDisplayMode,
  duplicate,
  removeComponent,
  setComponentSettingsMode,
  setComponentSelectedMode,
} = useCanvas()

const showUnnecessaryPageBreakWarning = computed((): boolean => {
  const currentIndex = canvasContent.value.findIndex((component) => component.id === props.id)
  if (currentIndex !== -1) {
    const prevSibling = canvasContent.value?.[currentIndex - 1]
    const nextSibling = canvasContent.value?.[currentIndex + 1]

    return (
      !prevSibling ||
      !nextSibling ||
      (prevSibling && [ComponentTypes.SectionList, ComponentTypes.PageBreak].includes(prevSibling.type)) ||
      (nextSibling && [ComponentTypes.SectionList, ComponentTypes.PageBreak].includes(nextSibling.type))
    )
  }
  return false
})

const componentPermissions = useComponentPermissions({
  id: computed(() => props.id),
  usedInSectionId: ref(''),
  when_used_in_section: ref(),
})
</script>
